import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch, ApiError } from '../../lib/api.js';

interface EnvVarRow {
  id: string;
  key: string;
  isSecret: boolean;
  value: string | null; // null = secret (write-only)
  updatedAt: string;
}

interface Draft {
  key: string;
  isSecret: boolean;
  value: string | null; // null = keep existing secret
  isNew?: boolean;
}

export function EnvVarsEditor({
  env,
  onClose,
}: {
  env: { id: string; name: string };
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const queryKey = ['environments', env.id, 'env-vars'];
  const { data: rows } = useQuery<EnvVarRow[]>({
    queryKey,
    queryFn: () => apiFetch(`/environments/${env.id}/env-vars`),
  });
  const [drafts, setDrafts] = useState<Draft[] | null>(null);
  const [deleted, setDeleted] = useState<string[]>([]);
  const [error, setError] = useState('');

  const current: Draft[] =
    drafts ?? (rows ?? []).map((r) => ({ key: r.key, isSecret: r.isSecret, value: r.isSecret ? null : r.value }));

  const setDraft = (index: number, patch: Partial<Draft>) => {
    const next = current.map((d, i) => (i === index ? { ...d, ...patch } : d));
    setDrafts(next);
  };

  const save = useMutation({
    mutationFn: () =>
      apiFetch(`/environments/${env.id}/env-vars`, {
        method: 'PUT',
        body: {
          vars: current
            .filter((d) => d.key)
            .map((d) => ({ key: d.key.toUpperCase(), isSecret: d.isSecret, value: d.value })),
          deleteKeys: deleted,
        },
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey });
      onClose();
    },
    onError: (err) => setError(err instanceof ApiError ? err.message : 'failed to save'),
  });

  return (
    <div className="dialog-backdrop" onClick={onClose}>
      <div className="card dialog" style={{ width: 'min(640px, calc(100vw - 48px))' }} onClick={(e) => e.stopPropagation()}>
        <h2>
          Environment variables — <span className="mono">{env.name}</span>
        </h2>
        <p className="dim">
          Written to <span className="mono">.env</span> (mode 0600) on each deploy. Secret values are
          write-only: leave a secret blank to keep its current value.
        </p>
        <div className="envvar-rows">
          {current.map((d, i) => (
            <div key={i} className="envvar-row">
              <input
                className="mono"
                placeholder="KEY"
                value={d.key}
                disabled={!d.isNew}
                onChange={(e) => setDraft(i, { key: e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, '') })}
              />
              <input
                className="mono"
                type={d.isSecret ? 'password' : 'text'}
                placeholder={d.isSecret && d.value === null ? '(unchanged)' : 'value'}
                value={d.value ?? ''}
                onChange={(e) => setDraft(i, { value: e.target.value })}
              />
              <label className="envvar-secret">
                <input
                  type="checkbox"
                  checked={d.isSecret}
                  onChange={(e) => setDraft(i, { isSecret: e.target.checked, value: d.value ?? '' })}
                />
                secret
              </label>
              <button
                className="btn btn-ghost"
                onClick={() => {
                  if (!d.isNew) setDeleted([...deleted, d.key]);
                  setDrafts(current.filter((_, j) => j !== i));
                }}
              >
                ✕
              </button>
            </div>
          ))}
        </div>
        <button
          className="btn btn-ghost"
          onClick={() => setDrafts([...current, { key: '', isSecret: true, value: '', isNew: true }])}
        >
          + Add variable
        </button>
        {deleted.length > 0 ? <p className="dim">will delete: {deleted.join(', ')}</p> : null}
        <p className="error-text">{error}</p>
        <div className="dialog-actions">
          <button className="btn btn-ghost" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-amber" disabled={save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
